import { FC, useMemo } from 'react'
import { Separator } from '@radix-ui/react-separator'
import { tokens } from '@/locales/translations/tokens'
import { Search, Globe, Check } from 'lucide-react'
import { Settings2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface ToolsDropdownProps {
  externalSearch: boolean
  deepSearch: boolean
  toggleExternalSearch: () => void
  toggleDeepSearch: () => void
  isLoading: boolean
}

export const ToolsDropdown: FC<ToolsDropdownProps> = ({
  externalSearch,
  deepSearch,
  toggleExternalSearch,
  toggleDeepSearch,
  isLoading,
}) => {
  const { t } = useTranslation()

  const ToolsOptions = useMemo(
    () => [
      {
        label: t(tokens.workstation.tools.externalSearch),
        icon: <Globe className='h-3.5 w-3.5' />,
        onClick: toggleExternalSearch,
        isActive: externalSearch,
      },
      {
        label: t(tokens.workstation.tools.deepSearch),
        icon: <Search className='h-3.5 w-3.5' />,
        onClick: toggleDeepSearch,
        isActive: deepSearch,
      },
    ],
    [externalSearch, deepSearch, toggleExternalSearch, toggleDeepSearch, t]
  )

  const activeTools = ToolsOptions.filter((option) => option.isActive)
  const hasActiveTools = activeTools.length > 0

  return (
    <div className='flex items-center gap-1'>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            type='button'
            variant='outline'
            className='h-7 rounded-full text-xs'
            size='sm'
            disabled={isLoading}
          >
            <Settings2 className='mr-1 h-3.5 w-3.5' />
            {!hasActiveTools && t(tokens.workstation.tools.title)}
            {hasActiveTools && (
              <span className='text-xs'>{activeTools.length}</span>
            )}

            {hasActiveTools && (
              <>
                <Separator
                  orientation='vertical'
                  className='mx-1 h-full w-0.5 bg-border'
                />
                <div className='flex items-center gap-2'>
                  {activeTools.map((option) => (
                    <span
                      key={option.label}
                      className='flex items-center justify-center truncate rounded-full'
                    >
                      {option.icon}
                    </span>
                  ))}
                </div>
              </>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className='w-56'
          align='start'
          side='top'
          forceMount
        >
          <DropdownMenuGroup>
            {ToolsOptions.map((option) => (
              <DropdownMenuItem key={option.label} onClick={option.onClick}>
                <div className='flex w-full items-center gap-2 text-xs'>
                  {option.icon}
                  {option.label}
                  {option.isActive && <Check className='ml-auto h-4 w-4' />}
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
